import { Fragment } from 'react'
import LegalLayout from './LegalLayout'

/**
 * Renders a legal text from a content module in ./content/ (see the header
 * comment there for the block format). To publish a new version of a
 * policy, replace its content module — no component changes needed.
 */
export default function LegalDocument({ doc, description }) {
  return (
    <LegalLayout title={doc.title.replace(/^OVACS /, '')} description={description}>
      <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">{doc.title}</h1>
      {doc.subtitle && <p className="text-gray-600 mb-3">{doc.subtitle}</p>}
      <p className="text-sm text-gray-500 mb-8">
        <strong>Last updated:</strong> {doc.lastUpdated}
        <span className="mx-2">·</span>
        <strong>Effective date:</strong> {doc.effectiveDate}
      </p>

      {doc.summary?.length > 0 && (
        <div className="bg-cyan-50 border-l-4 border-cyan-500 p-5 rounded mb-10 text-sm text-cyan-950 leading-relaxed space-y-1.5">
          <p className="font-semibold">In short</p>
          {doc.summary.map((t) => <p key={t}><Inline text={t} /></p>)}
        </div>
      )}

      <div className="text-gray-700 leading-relaxed text-[15px]">
        {groupBullets(doc.blocks).map((block, i) => <Block key={i} block={block} />)}
      </div>
    </LegalLayout>
  )
}

// Consecutive 'b' blocks become one <ul>.
function groupBullets(blocks) {
  const out = []
  for (const b of blocks) {
    const last = out[out.length - 1]
    if (b[0] === 'b' && last?.[0] === 'ul') last[1].push(b[1])
    else if (b[0] === 'b') out.push(['ul', [b[1]]])
    else out.push(b)
  }
  return out
}

function Block({ block }) {
  const [kind, ...args] = block
  switch (kind) {
    case 'h1':
      return (
        <h2 id={slugify(args[0])} className="text-xl sm:text-2xl font-semibold text-gray-900 mt-10 mb-3 scroll-mt-20">
          {args[0]}
        </h2>
      )
    case 'h2':
      return <h3 className="text-base font-semibold text-gray-900 mt-6 mb-2">{args[0]}</h3>
    case 'p':
      return <p className="mb-3"><Inline text={args[0]} /></p>
    case 'ul':
      return (
        <ul className="list-disc ps-6 mb-3 space-y-1.5">
          {args[0].map((t) => <li key={t}><Inline text={t} /></li>)}
        </ul>
      )
    case 'table': {
      const [fractions, header, rows] = args
      return (
        <div className="overflow-x-auto mb-5">
          <table className="min-w-full border border-gray-200 text-sm table-fixed">
            <colgroup>{fractions.map((f, i) => <col key={i} style={{ width: `${f * 100}%` }} />)}</colgroup>
            <thead className="bg-gray-50">
              <tr>{header.map((h) => <th key={h} className="text-start px-3 py-2 border-b font-semibold">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rows.map((r, ri) => (
                <tr key={ri}>{r.map((c, ci) => <td key={ci} className="px-3 py-2 align-top"><Inline text={c} /></td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )
    }
    default:
      return null
  }
}

// **bold**, URLs and email addresses. Trailing punctuation stays outside links.
const TOKEN = /(\*\*[\s\S]+?\*\*|https?:\/\/[^\s)]+[^\s).,;:]|[\w.+-]+@[\w-]+(?:\.[\w-]+)+)/g

function Inline({ text }) {
  return text.split(TOKEN).map((part, i) => {
    if (!part) return null
    if (part.startsWith('**')) return <strong key={i}>{part.slice(2, -2)}</strong>
    if (/^https?:\/\//.test(part)) {
      const internal = part.startsWith('https://ovacs.com/')
      return (
        <a key={i} href={internal ? part.slice('https://ovacs.com'.length) : part}
          {...(internal ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
          className="text-cyan-700 underline break-words">{part}</a>
      )
    }
    if (part.includes('@')) return <a key={i} href={`mailto:${part}`} className="text-cyan-700 underline">{part}</a>
    return <Fragment key={i}>{part}</Fragment>
  })
}

function slugify(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
}
